// Formatting + score-tier helpers shared across EI panels.
// Pure functions — depend on constants but never on React.

import { SCORE_COLORS, UNCLASSIFIED_LABEL } from "./constants";

export const scoreToTier = (score) => {
  const numericScore = Number(score || 0);
  if (numericScore >= 70) return "campaign";
  if (numericScore >= 50) return "quickwin";
  if (numericScore >= 30) return "monitor";
  return "low";
};

export const scoreTierColor = (score) => SCORE_COLORS[scoreToTier(score)];

/* ----------------------------- dates -----------------------------
 * Backend event dates (`start_date`, `end_date`, `week_of`) are plain
 * "YYYY-MM-DD" strings with no time or zone.
 *
 * ⚠️ Never pass one to `new Date(iso)`. ES spec parses a bare date-only string
 * as midnight UTC, but `.getDate()` / `.toLocaleString()` render in LOCAL time —
 * so every date shifts back a day for users west of UTC (all of the Americas):
 *   new Date("2026-07-04") at UTC-4 → renders "Jul 3"
 *   new Date("2026-08-01") at UTC-4 → renders "Jul 31"  (wrong month)
 *   new Date("2026-01-01") at UTC-4 → renders "Dec 31"  (wrong year)
 * The bug is invisible in UTC+ timezones, which is why it went unnoticed.
 *
 * `parseLocalDate` builds the date from its parts, so midnight is LOCAL midnight
 * and the calendar day always renders as written. Same approach as
 * competitive-intelligence/shared/format.js and WeeklyDensityPanel's
 * `weekKeyForDate`. Returns null for missing/malformed input.
 *
 * For real timestamps (e.g. `generated_at`, an ISO string with a time and zone)
 * `new Date()` is correct — do NOT route those through here.
 */
export const parseLocalDate = (iso) => {
  if (!iso) return null;
  const [year, month, day] = String(iso).slice(0, 10).split("-").map((part) => parseInt(part, 10));
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Sort key for a plain "YYYY-MM-DD" date, in local-midnight ms. `fallback` is
// returned for missing/unparseable values so comparators stay total.
export const dateSortKey = (iso, fallback = 0) => {
  const date = parseLocalDate(iso);
  return date ? date.getTime() : fallback;
};

// Local midnight today, as ms — the correct left-hand side for "is this date in
// the past?" comparisons against `dateSortKey`.
export const startOfTodayMs = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now.getTime();
};

export const formatMonthShort = (iso) => {
  const date = parseLocalDate(iso);
  if (!date) return "";
  return date.toLocaleString("en-US", { month: "short" }).toUpperCase();
};

export const formatDayOfMonth = (iso) => {
  const date = parseLocalDate(iso);
  if (!date) return "";
  return String(date.getDate());
};

export const formatAttendance = (attendance) => {
  const val = Number(attendance || 0);
  if (!val) return "";
  if (val >= 1000) return `~${(val / 1000).toFixed(val >= 10000 ? 0 : 1)}k`;
  return `~${val}`;
};

export const formatCategory = (cat) => {
  if (cat == null) return UNCLASSIFIED_LABEL;
  const trimmed = String(cat).trim();
  if (!trimmed) return UNCLASSIFIED_LABEL;
  if (/^undefined$/i.test(trimmed)) return UNCLASSIFIED_LABEL;
  return trimmed;
};

export const isUnclassifiedCategory = (cat) => formatCategory(cat) === UNCLASSIFIED_LABEL;

export const formatSubcategory = (sub) => {
  if (sub == null) return "";
  const trimmed = String(sub).trim();
  if (!trimmed) return "";
  if (/^undefined$/i.test(trimmed) || /^null$/i.test(trimmed) || /^others$/i.test(trimmed)) return "";
  return trimmed;
};
